/**
 * Doctor Rx list filters (D-02): the page URL ⇄ the controls ⇄ the API query.
 *
 * The filters live in the URL, so opening a prescription and coming back restores the list as it
 * was, and a filtered list can be bookmarked. Anything unrecognised in the URL falls back to the
 * default rather than reaching the API.
 */

import type { DoctorRxQuery } from "../api/doctor";
import { dateRange } from "./queueSearch";

export type RxStatus = "" | NonNullable<DoctorRxQuery["status"]>;
export type RxSchedule = "" | NonNullable<DoctorRxQuery["schedule_class"]>;
export type RxSource = "" | NonNullable<DoctorRxQuery["source"]>;
export type RxSort = NonNullable<DoctorRxQuery["sort"]>;

export interface DoctorRxFilters {
  q: string;
  status: RxStatus;
  schedule: RxSchedule;
  source: RxSource;
  from: string; // local calendar date yyyy-mm-dd, inclusive ("" = open)
  to: string; // local calendar date yyyy-mm-dd, inclusive ("" = open)
  sort: RxSort;
  desc: boolean;
  perPage: number;
}

export const PER_PAGE_OPTIONS = [25, 50, 100] as const;

export const STATUS_OPTIONS: { value: RxStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "consumed", label: "Used" },
];

export const SCHEDULE_OPTIONS: { value: RxSchedule; label: string }[] = [
  { value: "", label: "All" },
  { value: "none", label: "OTC" },
  { value: "H", label: "Schedule H" },
  { value: "H1", label: "Schedule H1" },
  { value: "X", label: "Schedule X" },
];

export const SOURCE_OPTIONS: { value: RxSource; label: string }[] = [
  { value: "", label: "All" },
  { value: "written", label: "Written by you" },
  { value: "digitized", label: "Digitised by pharmacy" },
];

export const SORT_OPTIONS: { value: RxSort; label: string }[] = [
  { value: "issued_at", label: "Issued date" },
  { value: "expires_at", label: "Valid till" },
  { value: "patient_name", label: "Patient name" },
];

export const DEFAULT_FILTERS: DoctorRxFilters = {
  q: "",
  status: "",
  schedule: "",
  source: "",
  from: "",
  to: "",
  sort: "issued_at",
  desc: true,
  perPage: 25,
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;

function oneOf<T extends string>(value: string | null, options: { value: T }[], fallback: T): T {
  return options.some((o) => o.value === value) ? (value as T) : fallback;
}

export function filtersFromParams(params: URLSearchParams): DoctorRxFilters {
  const perPage = Number(params.get("per_page"));
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  return {
    q: (params.get("q") ?? "").trim(),
    status: oneOf(params.get("status"), STATUS_OPTIONS, ""),
    schedule: oneOf(params.get("schedule"), SCHEDULE_OPTIONS, ""),
    source: oneOf(params.get("source"), SOURCE_OPTIONS, ""),
    from: DATE.test(from) ? from : "",
    to: DATE.test(to) ? to : "",
    sort: oneOf(params.get("sort"), SORT_OPTIONS, DEFAULT_FILTERS.sort),
    desc: params.get("order") !== "asc",
    perPage: (PER_PAGE_OPTIONS as readonly number[]).includes(perPage) ? perPage : DEFAULT_FILTERS.perPage,
  };
}

/** Only what differs from the defaults, so a plain list keeps a clean URL. */
export function filtersToParams(f: DoctorRxFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.status) p.set("status", f.status);
  if (f.schedule) p.set("schedule", f.schedule);
  if (f.source) p.set("source", f.source);
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.sort !== DEFAULT_FILTERS.sort) p.set("sort", f.sort);
  if (!f.desc) p.set("order", "asc");
  if (f.perPage !== DEFAULT_FILTERS.perPage) p.set("per_page", String(f.perPage));
  return p;
}

/** How many filters narrow the list (search, status, schedule, source, issue dates). */
export function activeFilterCount(f: DoctorRxFilters): number {
  return [f.q.trim(), f.status, f.schedule, f.source, f.from || f.to].filter(Boolean).length;
}

/** The filters with every narrowing filter cleared; sort and page size are kept. */
export function clearedFilters(f: DoctorRxFilters): DoctorRxFilters {
  return { ...DEFAULT_FILTERS, sort: f.sort, desc: f.desc, perPage: f.perPage };
}

/** The API query for the filters (without the page), or null when the date range is backwards. */
export function toApiQuery(f: DoctorRxFilters): Omit<DoctorRxQuery, "page"> | null {
  const range = dateRange(f.from, f.to);
  if (range === null) return null;
  return {
    q: f.q.trim() || undefined,
    status: f.status || undefined,
    schedule_class: f.schedule || undefined,
    source: f.source || undefined,
    issued_from: range.created_from,
    issued_to: range.created_to,
    sort: f.sort,
    descending: f.desc,
    per_page: f.perPage,
  };
}
